import React, { useState } from 'react';

export type TeamJerseySize = 'xs' | 'sm' | 'md' | 'lg';

interface TeamJerseyProps {
  jerseyUrl?: string;
  /** Size at all widths, or below `md` when `sizeMd` is also given. */
  size?: TeamJerseySize;
  /**
   * Size from the `md` breakpoint (768px) up. Use with a smaller `size` to run a
   * compact layout on phones while leaving desktop untouched (DKPLAT-663).
   *
   * Declared here rather than as a `md:w-[…]` class on each caller so the pixel
   * values stay with the component that owns them. The class-on-caller approach
   * also fails silently: those are Tailwind *arbitrary* values, so if the
   * generator hasn't rescanned the file, the class is simply never emitted and
   * the element quietly keeps the smaller base size with no error anywhere.
   */
  sizeMd?: TeamJerseySize;
  className?: string;
}

const SIZE_CLASSES: Record<TeamJerseySize, string> = {
  xs: 'w-[48px] h-[48px]',
  sm: 'w-[60px] h-[60px]',
  md: 'w-[100px] h-[100px]',
  lg: 'w-[140px] h-[140px]',
};

// Full literals, never built by interpolation — Tailwind only emits classes it
// can find as complete strings in the source.
const SIZE_CLASSES_MD: Record<TeamJerseySize, string> = {
  xs: 'md:w-[48px] md:h-[48px]',
  sm: 'md:w-[60px] md:h-[60px]',
  md: 'md:w-[100px] md:h-[100px]',
  lg: 'md:w-[140px] md:h-[140px]',
};

const TeamJersey: React.FC<TeamJerseyProps> = ({
  jerseyUrl,
  size = 'md',
  sizeMd,
  className = '',
}) => {
  const [hasError, setHasError] = useState(false);

  // Don't render anything if no jersey URL is provided or image failed to load
  if (!jerseyUrl || hasError) {
    return null;
  }

  const responsive = sizeMd ? ` ${SIZE_CLASSES_MD[sizeMd]}` : '';

  return (
    <img
      src={jerseyUrl}
      alt="Team Jersey"
      className={`${SIZE_CLASSES[size]}${responsive} object-contain ${className}`}
      onError={() => setHasError(true)}
    />
  );
};

export default TeamJersey;
